import { useEffect, useState } from 'react';
import { api, errMsg } from '../lib/api';
import { Icon } from '../lib/icons';

interface NotaryState {
  notarized: boolean;
  record: {
    contentHash: string;
    txHash: string | null;
    blockNumber: number | null;
    chain: string;
    notarizedAt: number | null;
  } | null;
  pending: boolean;
}

/** Base 主网区块浏览器；测试网换 base-sepolia */
function explorerUrl(chain: string, txHash: string): string {
  const host = chain === 'base-sepolia' ? 'sepolia.basescan.org' : 'basescan.org';
  return `https://${host}/tx/${txHash}`;
}

/**
 * 链上存证徽章。
 *
 * 核心设计：所有信息都可在区块浏览器自行查验。
 * 客户能亲眼看到那条记录，这比任何自夸的文案都更有说服力。
 */
export default function NotaryBadge({
  memorialId,
  productId,
  canBuy = false,
}: {
  memorialId: string;
  /** 存证套餐商品 id，用于未存证时发起购买 */
  productId?: string;
  /** 是否显示购买入口。仅馆主页面传 true，访客参观时不显示购买按钮 */
  canBuy?: boolean;
}) {
  const [state, setState] = useState<NotaryState | null>(null);
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    const [s, a] = await Promise.all([
      api<NotaryState>(`/notary/memorial/${memorialId}`),
      api<{ available: boolean }>('/notary/status'),
    ]);
    if (s.ok && s.data) setState(s.data);
    if (a.ok && a.data) setAvailable(a.data.available);
  }

  useEffect(() => {
    load();
  }, [memorialId]);

  async function buy() {
    if (!productId) {
      setError('存证套餐暂未配置');
      return;
    }
    setBuying(true);
    setError('');

    const res = await api<{ orderId: string; payUrl: string }>('/orders', {
      method: 'POST',
      body: JSON.stringify({ memorialId, productId, quantity: 1 }),
    });

    setBuying(false);

    if (res.ok && res.data) {
      window.location.href = res.data.payUrl;
    } else {
      setError(errMsg(res, '下单失败，请先登录'));
    }
  }

  // 功能未开放时完全不渲染
  if (!available) return null;

  // 已排队等待上链
  if (state?.pending && !state.notarized) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-center">
        <p className="flex items-center gap-1 text-xs text-amber-700"><Icon name="chain" size={14} /> 存证排队中</p>
        <p className="mt-1 text-[11px] text-amber-600">
          内容指纹已生成，正在等待写入区块链，请稍后查看
        </p>
      </div>
    );
  }

  // 尚未存证：仅馆主页面显示购买入口，访客参观页不显示
  if (!state?.notarized) {
    if (!canBuy) return null;
    return (
      <div className="rounded-2xl border border-ink-200 bg-white/80 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-ink-900">链上存证 · 永久印记</p>
            <p className="mt-1 text-[11px] leading-relaxed text-ink-500">
              把纪念馆的内容指纹写入 Base 链，最终锚定至以太坊主网。
              全球数十万节点共同记录，任何机构都无法单独修改或删除。
            </p>
          </div>
          <button
            onClick={buy}
            disabled={buying}
            className="shrink-0 rounded-xl bg-ink-900 px-5 py-2 text-xs text-ink-50 disabled:opacity-40"
          >
            {buying ? '处理中…' : '立即存证'}
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  // 已存证：徽章 + 详情
  const r = state.record;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full rounded-2xl border border-[var(--c-accent)]/30 bg-[var(--c-accent-soft)] px-5 py-4 text-center transition hover:border-[var(--c-accent)]/60"
      >
        <p className="flex items-center gap-1 text-xs text-[var(--c-accent)]"><Icon name="chain" size={14} /> 链上存证 · 已锚定以太坊</p>
        {r?.blockNumber && (
          <p className="mt-1 font-mono text-[10px] text-[var(--c-muted)]">
            区块 #{r.blockNumber} · {r.chain}
          </p>
        )}
        <p className="mt-1 text-[10px] text-[var(--c-muted)]">点击查看与自行查验 →</p>
      </button>

      {open && r && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serifcn text-lg text-ink-900">链上存证</h3>
            <p className="mt-2 text-xs leading-relaxed text-ink-600">
              内容指纹是根据纪念馆全部内容算出的一串唯一编码，被记录在区块链上。
              任何机构——包括我们——都无法单独修改或删除它。
            </p>

            <dl className="mt-5 space-y-3 text-xs">
              <div>
                <dt className="text-ink-500">内容指纹</dt>
                <dd className="mt-0.5 break-all font-mono text-[11px] text-ink-800">
                  {r.contentHash}
                </dd>
              </div>
              {r.txHash && (
                <div>
                  <dt className="text-ink-500">交易哈希</dt>
                  <dd className="mt-0.5 break-all font-mono text-[11px] text-ink-800">
                    {r.txHash}
                  </dd>
                </div>
              )}
              {r.blockNumber != null && (
                <div>
                  <dt className="text-ink-500">区块高度</dt>
                  <dd className="mt-0.5 font-mono text-[11px] text-ink-800">
                    #{r.blockNumber}
                  </dd>
                </div>
              )}
              {r.notarizedAt && (
                <div>
                  <dt className="text-ink-500">存证时间</dt>
                  <dd className="mt-0.5 text-[11px] text-ink-800">
                    {new Date(r.notarizedAt * 1000).toLocaleString('zh-CN')}
                  </dd>
                </div>
              )}
            </dl>

            {r.txHash && (
              <a
                href={explorerUrl(r.chain, r.txHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 block h-11 rounded-xl bg-ink-900 text-center text-sm leading-[44px] text-ink-50"
              >
                在区块浏览器上自行查验 ↗
              </a>
            )}

            <p className="mt-4 text-[10px] leading-relaxed text-ink-400">
              说明：链上记录的是内容指纹，不含任何个人信息。
              你随时可导出完整的纪念册自行保存。
            </p>

            <button
              onClick={() => setOpen(false)}
              className="mt-2 h-11 w-full text-sm text-ink-500"
            >
              关闭
            </button>
          </div>
        </div>
      )}
    </>
  );
}
